/**
 * How Reports → Mobile Usage prints a number.
 *
 * These were local to MobileUsageReportPage until the card drill-downs arrived: the window
 * that opens behind a number has to print the same figure the same way, or the list and the
 * card above it look like two different reports. They live here so there is ONE rounding
 * rule per unit, not one per component.
 *
 * The page's language rules apply to everything here (see MobileUsageReportPage's header):
 * plain units, no percentile words, and a decimal only where it changes the meaning.
 */

/** Anything off an HTTP response, as a number. A non-number is 0, never NaN on screen. */
export const n = (v: unknown): number => {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
};

/** Minutes with their unit. Under ten minutes the decimal matters (7.2 min is a very
 *  different day from 7.9 min); above it, it is noise. */
export const minutes = (v: unknown): string => {
  const x = n(v);
  return `${x < 10 ? Math.round(x * 10) / 10 : Math.round(x)} min`;
};

export const people = (v: unknown): string => {
  const x = Math.round(n(v));
  return `${x.toLocaleString()} ${x === 1 ? 'person' : 'people'}`;
};

export const days = (v: unknown): string => {
  const x = Math.round(n(v) * 10) / 10;
  return `${x} ${x === 1 ? 'day' : 'days'}`;
};

/** Seconds up to a minute and a half, then minutes: "40 seconds", "3.5 min". */
export const seconds = (v: unknown): string => {
  const s = Math.round(n(v));
  if (s < 90) return `${s} ${s === 1 ? 'second' : 'seconds'}`;
  return minutes(s / 60);
};

/** A plain whole number, grouped for reading. */
export const count = (v: unknown): string => Math.round(n(v)).toLocaleString();
